import React, { useState } from "react";
import { AiFillEye } from "react-icons/ai";
import { FaTrash } from "react-icons/fa";
import Button from "../../../../components/Button";
import styles from "../css/BeneficiaryCard.module.css";
import inputStyles from "../../../../css/components/formInput.module.css";
import dropdownStyles from "../../../../css/components/dropdown.module.css";

export default function BeneficiaryCard({ index, value, onChange, onRemove, isUpdateMode = false, hideActions = false, onRequestDisable, onRequestToggle, isHidden = false, mainHome = {}, mainDelivery = {} }) {
  const [suppressLift, setSuppressLift] = useState(false);
  const handleChange = (field, v) => {
    onChange(index, { ...value, [field]: v });
  };

  const deriveDobFromSaId = (id) => {
    const clean = String(id || "").replace(/[^0-9]/g, "");
    if (clean.length < 6) return "";
    const yy = clean.slice(0, 2);
    const mm = clean.slice(2, 4);
    const dd = clean.slice(4, 6);
    const yearNum = Number(yy);
    if (!Number.isFinite(yearNum)) return "";
    const currentYear = new Date().getFullYear() % 100;
    const century = yearNum > currentYear ? 1900 : 2000;
    const fullYear = century + yearNum;
    return `${fullYear.toString().padStart(4, "0")}-${mm}-${dd}`;
  };

  const deriveSexFromSaId = (id) => {
    const clean = String(id || "").replace(/[^0-9]/g, "");
    if (clean.length < 10) return "";
    const genderDigits = Number(clean.slice(6, 10));
    if (!Number.isFinite(genderDigits)) return "";
    return genderDigits >= 5000 ? "Male" : "Female";
  };

  const deriveAgeFromDob = (dob) => {
    if (!dob) return "";
    const d = new Date(dob);
    if (Number.isNaN(d.getTime())) return "";
    const today = new Date();
    let age = today.getFullYear() - d.getFullYear();
    const m = today.getMonth() - d.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < d.getDate())) {
      age -= 1;
    }
    return age >= 0 ? age : "";
  };

  const handleAddCondition = () => {
    const next = [...(value.conditionsExtra || []), ""];
    onChange(index, { ...value, conditionsExtra: next });
  };

  const handleChangeCondition = (i, v) => {
    const list = [...(value.conditionsExtra || [])];
    list[i] = v;
    onChange(index, { ...value, conditionsExtra: list });
  };

  const handleRemoveCondition = (i) => {
    const list = [...(value.conditionsExtra || [])];
    list.splice(i, 1);
    onChange(index, { ...value, conditionsExtra: list });
  };

  const addAllergy = () => {
    const next = [...(value.allergiesList || []), ""];
    onChange(index, { ...value, allergiesList: next });
  };
  const changeAllergy = (i, v) => {
    const list = [...(value.allergiesList || [])];
    list[i] = v;
    onChange(index, { ...value, allergiesList: list });
  };
  const removeAllergy = (i) => {
    const list = [...(value.allergiesList || [])];
    list.splice(i, 1);
    onChange(index, { ...value, allergiesList: list });
  };

  const addDeliveryAddress = () => {
    const next = [...(value.deliveryAddressesExtra || []), { street: "", suburb: "", city: "", postalCode: "" }];
    onChange(index, { ...value, deliveryAddressesExtra: next });
  };
  const changeDeliveryAddress = (i, field, v) => {
    const list = [...(value.deliveryAddressesExtra || [])];
    list[i] = { ...(list[i] || { street: "", suburb: "", city: "", postalCode: "" }), [field]: v };
    onChange(index, { ...value, deliveryAddressesExtra: list });
  };
  const removeDeliveryAddress = (i) => {
    const list = [...(value.deliveryAddressesExtra || [])];
    list.splice(i, 1);
    onChange(index, { ...value, deliveryAddressesExtra: list });
  };

  return (
    <div className={`${styles.beneficiaryCard} ${isHidden ? styles.hiddenCard : ""} ${suppressLift ? styles.noLift : ""}`}>
      <div className={styles.cardHeaderRow}>
        <span className={styles.cardIndex}>#{index + 1}</span>
        {hideActions ? null : (
          isUpdateMode ? (
            <button
              className={styles.eyeBtn}
              aria-label="Disable"
              onClick={() => {
                if (typeof onRequestToggle === 'function') {
                  onRequestToggle(index, isHidden);
                } else if (typeof onRequestDisable === 'function') {
                  onRequestDisable(index);
                } else {
                  onRemove(index);
                }
              }}
            >
              <AiFillEye />
            </button>
          ) : (
            <button
              className={styles.trashBtn}
              aria-label="Remove"
              onClick={() => onRemove(index)}
              onMouseEnter={() => setSuppressLift(true)}
              onMouseLeave={() => setSuppressLift(false)}
              onFocus={() => setSuppressLift(true)}
              onBlur={() => setSuppressLift(false)}
            >
              <FaTrash />
            </button>
          )
        )}
      </div>
      <fieldset disabled={hideActions} className={styles.fieldsetReset}>
        <div className={styles.inlineThree}>
          <div className={inputStyles.formGroup}>
            <label>Surname</label>
            <input className={inputStyles.formInput} type="text" value={value.surname || ""} onChange={(e) => handleChange("surname", e.target.value)} />
          </div>
          <div className={inputStyles.formGroup}>
            <label>Title</label>
            <select className={dropdownStyles.dropdown} value={value.title || ""} onChange={(e) => handleChange("title", e.target.value)}>
              <option value="" disabled hidden>Select</option>
              <option>Mr</option>
              <option>Mrs</option>
              <option>Miss</option>
              <option>Dr</option>
            </select>
          </div>
        </div>

        <div className={styles.inlineTwo}>
          <div className={inputStyles.formGroup}>
            <label>First Name(s)</label>
            <input className={inputStyles.formInput} type="text" value={value.firstNames || ""} onChange={(e) => handleChange("firstNames", e.target.value)} />
          </div>
          <div className={inputStyles.formGroup}>
            <label>Date Of Birth</label>
            <input className={inputStyles.formInput} type="date" value={value.dob || ""} onChange={(e) => handleChange("dob", e.target.value)} />
          </div>
        </div>

        <div className={styles.inlineTwo}>
          <div className={inputStyles.formGroup}>
            <label>ID Number / Passport</label>
            <div className={styles.idRow}>
              <div className={styles.idTypeGroup}>
                <label className={styles.idTypeLabel}>
                  <input
                    type="radio"
                    name={`ben-id-type-${index}`}
                    value="id"
                    checked={(value.idType || "id") === "id"}
                    className={styles.idTypeRadio}
                    onChange={() => handleChange("idType", "id")}
                  />
                  ID Number
                </label>
                <label className={styles.idTypeLabel}>
                  <input
                    type="radio"
                    name={`ben-id-type-${index}`}
                    value="passport"
                    checked={value.idType === "passport"}
                    className={styles.idTypeRadio}
                    onChange={() => handleChange("idType", "passport")}
                  />
                  Passport
                </label>
              </div>
              <input
                className={`${inputStyles.formInput} ${styles.idNumberInput}`}
                type="text"
                value={value.idNumber || ""}
                onChange={(e) => {
                  const raw = e.target.value || '';
                  const isId = (value.idType || 'id') === 'id';
                  const v = isId ? raw.replace(/\D/g, '').slice(0, 13) : raw;
                  let next = { ...value, idNumber: v };

                  if (isId && v.length === 13) {
                    const derivedDob = deriveDobFromSaId(v);
                    const derivedSex = deriveSexFromSaId(v);
                    const derivedAge = deriveAgeFromDob(derivedDob);

                    if (derivedDob) {
                      next = { ...next, dob: derivedDob };
                    }
                    if (derivedSex) {
                      next = { ...next, sex: derivedSex };
                    }
                    if (derivedAge !== '') {
                      next = { ...next, age: derivedAge };
                    }
                  }

                  onChange(index, next);
                }}
              />
            </div>
          </div>
        </div>

        <div className={styles.inlineTwo}>
          <div className={inputStyles.formGroup}>
            <label>Home/Cell Number</label>
            <input
              className={inputStyles.formInput}
              type="tel"
              value={value.cell || ""}
              onChange={(e) => {
                const v = (e.target.value || '').replace(/\D/g, '').slice(0, 10);
                handleChange("cell", v);
              }}
            />
          </div>
          <div className={inputStyles.formGroup}>
            <label>Dependent Code</label>
            <input
              className={inputStyles.formInput}
              type="text"
              value={value.dependentCode || ""}
              onChange={(e) => {
                const v = (e.target.value || '').replace(/\D/g, '').slice(0, 2);
                handleChange("dependentCode", v);
              }}
            />
          </div>
        </div>

        <div className={styles.inlineThree}>
          <div className={inputStyles.formGroup}>
            <label>Age</label>
            <input className={inputStyles.formInput} type="number" min="0" value={value.age ?? ""} onChange={(e) => {
              const n = Math.max(0, Number(e.target.value || 0));
              handleChange("age", Number.isFinite(n) ? n : 0);
            }} />
          </div>
          <div className={inputStyles.formGroup}>
            <label>Weight (Kg)</label>
            <input className={inputStyles.formInput} type="number" min="0" step="0.01" value={value.weight ?? ""} onChange={(e) => {
              const value = e.target.value;
              if (value === "" || value === ".") {
                handleChange("weight", value);
                return;
              }
              const n = Math.max(0, parseFloat(value));
              handleChange("weight", Number.isFinite(n) ? n : 0);
            }} />
          </div>
          <div className={inputStyles.formGroup}>
            <label>Sex</label>
            <select className={dropdownStyles.dropdown} value={value.sex || ""} onChange={(e) => handleChange("sex", e.target.value)}>
              <option value="" disabled hidden>Select</option>
              <option>Male</option>
              <option>Female</option>
              <option>Other</option>
            </select>
          </div>
        </div>

        <div className={styles.inlineWithButton}>
          <div className={`${inputStyles.formGroup} ${styles.grow}`}>
            <label>Chronic Conditions</label>
            <input
              className={inputStyles.formInput}
              type="text"
              value={value.conditions || ""}
              onChange={(e) => {
                const v = (e.target.value || '').replace(/[^a-zA-Z\s,]/g, '');
                handleChange("conditions", v);
              }}
            />
          </div>
          {!hideActions && (
            <Button
              variant="primary"
              className={styles.plusBtn}
              onClick={handleAddCondition}
            >
              +
            </Button>
          )}
        </div>

        {Array.isArray(value.conditionsExtra) && value.conditionsExtra.length > 0 && (
          <div className={styles.chronicList}>
            {value.conditionsExtra.map((c, i) => (
              <div key={i} className={styles.conditionRow}>
                <div className={`${inputStyles.formGroup} ${styles.grow}`}>
                  <label>Additional Chronic Condition {i + 1}</label>
                  <input
                    className={inputStyles.formInput}
                    type="text"
                    value={c}
                    onChange={(e) => {
                      const v = (e.target.value || '').replace(/[^a-zA-Z\s,]/g, '');
                      handleChangeCondition(i, v);
                    }}
                  />
                </div>
                {!hideActions && (
                  <button
                    type="button"
                    className={styles.chronicDeleteBtn}
                    aria-label={`Remove chronic condition ${i + 1}`}
                    onClick={() => handleRemoveCondition(i)}
                    title="Remove condition"
                  >
                    <FaTrash />
                  </button>
                )}
              </div>
            ))}
          </div>
        )}

        <div className={styles.inlineWithButton}>
          <div className={`${inputStyles.formGroup} ${styles.grow}`}>
            <label>Allergies</label>
            <input
              className={inputStyles.formInput}
              type="text"
              value={(value.allergiesList && value.allergiesList[0]) || ""}
              onChange={(e) => changeAllergy(0, e.target.value)}
            />
          </div>
          {!hideActions && (
            <Button
              variant="primary"
              className={styles.plusBtn}
              onClick={addAllergy}
              title="Add Allergy"
            >
              +
            </Button>
          )}
        </div>

        {(value.allergiesList || []).slice(1).map((a, i) => (
          <div key={i} className={styles.inlineWithButton}>
            <div className={`${inputStyles.formGroup} ${styles.grow}`}>
              <label>{`Additional Allergy ${i + 1}`}</label>
              <input
                className={inputStyles.formInput}
                type="text"
                value={a}
                onChange={(e) => changeAllergy(i + 1, e.target.value)}
              />
            </div>
            {!hideActions && (
              <button
                type="button"
                className={styles.chronicDeleteBtn}
                aria-label={`Remove allergy ${i + 1}`}
                onClick={() => removeAllergy(i + 1)}
                title="Remove allergy"
              >
                <FaTrash />
              </button>
            )}
          </div>
        ))}

        <div className={styles.addresses}>
          <div className={styles.addressGroup}>
            <h4>Home Address</h4>
            <div className={styles.addressHeaderRow}>
              <span></span>
            </div>
            <div className={styles.addressFields}>
              <div className={inputStyles.formGroup}>
                <label>Street</label>
                <input
                  className={inputStyles.formInput}
                  type="text"
                  value={value.homeStreet || ""}
                  onChange={(e) => handleChange("homeStreet", e.target.value)}
                />
              </div>
              <div className={inputStyles.formGroup}>
                <label>Suburb</label>
                <input
                  className={inputStyles.formInput}
                  type="text"
                  value={value.homeSuburb || ""}
                  onChange={(e) => {
                    const v = (e.target.value || '').replace(/[^a-zA-Z\s]/g, '');
                    handleChange("homeSuburb", v);
                  }}
                />
              </div>
              <div className={inputStyles.formGroup}>
                <label>City</label>
                <input
                  className={inputStyles.formInput}
                  type="text"
                  value={value.homeCity || ""}
                  onChange={(e) => handleChange("homeCity", e.target.value)}
                />
              </div>
              <div className={inputStyles.formGroup}>
                <label>Postal Code</label>
                <input className={inputStyles.formInput} type="text" maxLength={4} value={value.homePostalCode || ""} onChange={(e) => {
                  const v = (e.target.value || '').replace(/\D/g, '').slice(0, 4);
                  handleChange("homePostalCode", v);
                }} />
              </div>
            </div>
          </div>

          <div className={styles.addressGroup}>
            <div className={styles.addressHeaderRow}>
              <h4>Delivery Address</h4>
              {!hideActions && (
                <Button
                  variant="primary"
                  className={styles.plusBtn}
                  onClick={addDeliveryAddress}
                  title="Add Delivery Address"
                >
                  +
                </Button>
              )}
            </div>
            <div className={styles.addressFields}>
              <div className={inputStyles.formGroup}>
                <label>Street</label>
                <input
                  className={inputStyles.formInput}
                  type="text"
                  value={value.deliveryStreet || ""}
                  onChange={(e) => handleChange("deliveryStreet", e.target.value)}
                />
              </div>
              <div className={inputStyles.formGroup}>
                <label>Suburb</label>
                <input
                  className={inputStyles.formInput}
                  type="text"
                  value={value.deliverySuburb || ""}
                  onChange={(e) => {
                    const v = (e.target.value || '').replace(/[^a-zA-Z\s]/g, '');
                    handleChange("deliverySuburb", v);
                  }}
                />
              </div>
              <div className={inputStyles.formGroup}>
                <label>City</label>
                <input
                  className={inputStyles.formInput}
                  type="text"
                  value={value.deliveryCity || ""}
                  onChange={(e) => handleChange("deliveryCity", e.target.value)}
                />
              </div>
              <div className={inputStyles.formGroup}>
                <label>Postal Code</label>
                <input
                  className={inputStyles.formInput}
                  type="text"
                  maxLength={4}
                  value={value.deliveryPostalCode || ""}
                  onChange={(e) => {
                    const v = (e.target.value || '').replace(/\D/g, '').slice(0, 4);
                    handleChange("deliveryPostalCode", v);
                  }}
                />
              </div>
            </div>

            {(value.deliveryAddressesExtra || []).map((addr, i) => (
              <div key={i} className={`${styles.addressFields} ${styles.addressExtraGroup}`}>
                <div className={inputStyles.formGroup}>
                  <label>Street</label>
                  <input className={inputStyles.formInput} type="text" value={addr.street || ""} onChange={(e) => changeDeliveryAddress(i, 'street', e.target.value)} />
                </div>
                <div className={inputStyles.formGroup}>
                  <label>Suburb</label>
                  <input className={inputStyles.formInput} type="text" value={addr.suburb || ""} onChange={(e) => changeDeliveryAddress(i, 'suburb', e.target.value)} />
                </div>
                <div className={inputStyles.formGroup}>
                  <label>City</label>
                  <input className={inputStyles.formInput} type="text" value={addr.city || ""} onChange={(e) => changeDeliveryAddress(i, 'city', e.target.value)} />
                </div>
                <div className={inputStyles.formGroup}>
                  <label>Postal Code</label>
                  <input className={inputStyles.formInput} type="text" maxLength={4} value={addr.postalCode || addr.postal || ""} onChange={(e) => {
                    const v = (e.target.value || '').replace(/\D/g, '').slice(0, 4);
                    changeDeliveryAddress(i, 'postalCode', v);
                  }} />
                </div>
                {!hideActions && (
                  <button
                    type="button"
                    className={styles.chronicDeleteBtn}
                    aria-label={`Remove delivery address ${i + 1}`}
                    onClick={() => removeDeliveryAddress(i)}
                    title="Remove delivery address"
                  >
                    <FaTrash />
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      </fieldset>
    </div>
  );
}